import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useContentMulti } from "@/hooks/useContent";
import { Quote, Star } from "lucide-react";

interface Testimonial {
  id: string;
  author_name: string;
  author_title: string;
  author_company: string;
  content: string;
  rating: number;
  avatar_url: string | null;
}

const TestimonialsSection = () => {
  const c = useContentMulti({
    "v2.testimonials.title": "What Our Clients Say",
    "v2.testimonials.subtitle": "Trusted by businesses across Southern California for reliable IT services and cybersecurity solutions.",
  });

  const { data: testimonials = [] } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials")
        .select("id, author_name, author_title, author_company, content, rating, avatar_url")
        .eq("enabled", true)
        .order("sort_order");
      if (error) throw error;
      return data as Testimonial[];
    },
    staleTime: 5 * 60 * 1000,
  });

  if (testimonials.length === 0) return null;

  // Duplicate for seamless loop
  const cards = [...testimonials, ...testimonials];

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto px-4 mb-12 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-3">
          {c["v2.testimonials.title"]}
        </h2>
        <div className="w-12 h-1 bg-primary mx-auto mb-4 rounded-full" />
        <p className="text-slate-600 max-w-2xl mx-auto">
          {c["v2.testimonials.subtitle"]}
        </p>
      </div>

      {/* Marquee container */}
      <div className="relative">
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

        <div className="flex animate-marquee gap-6 w-max">
          {cards.map((t, i) => (
            <div
              key={`${t.id}-${i}`}
              className="w-[340px] h-[280px] flex-shrink-0 bg-slate-50 rounded-2xl p-6 border border-slate-100 shadow-sm flex flex-col"
            >
              {/* Quote icon */}
              <Quote className="h-8 w-8 text-primary/30 mb-3" />

              {/* Stars */}
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: 5 }).map((_, si) => (
                  <Star
                    key={si}
                    className={`h-4 w-4 ${si < t.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
                  />
                ))}
              </div>

              {/* Content */}
              <p className="text-slate-700 text-sm leading-relaxed flex-1 line-clamp-4">
                "{t.content}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 mt-auto">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary to-red-400 flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {t.avatar_url ? (
                    <img src={t.avatar_url} alt={t.author_name} className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    t.author_name.charAt(0)
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{t.author_name}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {t.author_title}{t.author_company ? `, ${t.author_company}` : ""}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
